/* eslint-disable react-hooks/exhaustive-deps */
import React, { useMemo } from "react";
import { MdCheckBox, MdCheckBoxOutlineBlank } from "react-icons/md";

/**
 * BestTimeToVisit — visual-only refresh (white mode)
 * - Keeps props and external behavior intact.
 * - Adds: selected count, Select All / Clear / Invert shortcuts.
 * - Accessible: buttons announce pressed state; focus-visible rings.
 * - Still stores lowercase month strings via setBestTime([...]).
 */
const BestTimeToVisit = ({ bestTime, setBestTime }) => {
  const months = [
    "january", "february", "march", "april", "may", "june",
    "july", "august", "september", "october", "november", "december",
  ];

  // Normalize incoming value (array or CSV string) ➜ lowercase month array
  const selected = useMemo(() => {
    const raw = String(bestTime ?? "").toLowerCase().split(",");
    const normalized = raw
      .map((m) => m.trim())
      .filter((m) => months.includes(m));
    // If bestTime is already an array, include those too
    if (Array.isArray(bestTime)) {
      bestTime.forEach((m) => {
        const v = String(m).toLowerCase().trim();
        if (months.includes(v) && !normalized.includes(v)) normalized.push(v);
      });
    }
    return normalized;
  }, [bestTime]);

  const removeFromBest = (m) => {
    // preserve external shape: array of lowercase months
    setBestTime(selected.filter((b) => b !== m.toLowerCase()));
  };

  const addToBest = (m) => {
    const v = m.toLowerCase();
    if (!selected.includes(v)) setBestTime([...selected, v]);
  };

  const toggleMonth = (m) => {
    selected.includes(m.toLowerCase()) ? removeFromBest(m) : addToBest(m);
  };

  // Quick actions (visual conveniences, same underlying data)
  const selectAll = () => setBestTime([...months]);
  const clearAll = () => setBestTime([]);
  const invertAll = () =>
    setBestTime(months.filter((m) => !selected.includes(m)));

  return (
    <section className="w-full">
      <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
        {/* Header */}
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-sm font-semibold tracking-wide text-gray-900">
              Best time to visit
            </h3>
            <p className="text-xs text-gray-600">
              Select the months when this experience is most recommended.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="rounded-full border border-gray-200 px-2.5 py-1 text-xs text-gray-700">
              {selected.length} / 12 selected
            </span>
            <div className="hidden sm:flex items-center gap-1">
              <button
                type="button"
                onClick={selectAll}
                className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                Select all
              </button>
              <button
                type="button"
                onClick={clearAll}
                className="rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={invertAll}
                className="rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
              >
                Invert
              </button>
            </div>
          </div>
        </div>

        {/* Month chips */}
        <div
          role="group"
          aria-label="Select best months to visit"
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2"
        >
          {months.map((m) => {
            const isSelected = selected.includes(m);
            return (
              <button
                key={m}
                type="button"
                onClick={() => toggleMonth(m)}
                aria-pressed={isSelected}
                className={[
                  "group relative inline-flex items-center justify-between gap-2",
                  "rounded-lg border px-3 py-2 text-sm capitalize transition",
                  "focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500",
                  isSelected
                    ? "border-emerald-300 bg-emerald-50 text-emerald-800 shadow-sm"
                    : "border-gray-200 bg-white text-gray-800 hover:bg-gray-50",
                ].join(" ")}
              >
                <span className="truncate">{m}</span>
                {isSelected ? (
                  <MdCheckBox className="h-5 w-5 text-emerald-600" />
                ) : (
                  <MdCheckBoxOutlineBlank className="h-5 w-5 text-gray-400 group-hover:text-gray-500" />
                )}
              </button>
            );
          })}
        </div>

        {/* Footer / helper text */}
        <p className="mt-3 text-[11px] text-gray-600">
          Tip: Choose multiple months to help travelers plan around seasons and weather.
        </p>

        {/* Mobile quick actions */}
        <div className="mt-3 flex items-center justify-end gap-2 sm:hidden">
          <button
            type="button"
            onClick={selectAll}
            className="rounded-md px-2 py-1 text-xs font-medium text-emerald-700 hover:bg-emerald-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            Select all
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            Clear
          </button>
          <button
            type="button"
            onClick={invertAll}
            className="rounded-md px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
          >
            Invert
          </button>
        </div>
      </div>
    </section>
  );
};

export default BestTimeToVisit;
